/**
 * AI-style help panel for an onboarding card.
 *
 * Every StepCard renders this: a Sparkles icon that opens a right-side
 * slide-over explaining what the step does and the concrete conditions to
 * reach "done". The explanation "streams" in word-by-word for an AI feel
 * (honoring prefers-reduced-motion). Content is authored per step in
 * lib/onboarding/help.ts and falls back to the registry description.
 */
import { useEffect, useState } from 'react'
import { Sparkles } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import type { OnboardingTask } from '@limo/database'
import { getStepDefinition } from '@/lib/onboarding/steps'
import { getStepHelp } from '@/lib/onboarding/help'
import { StepStatusBadge } from './StepStatusBadge'

/**
 * Reveal `text` word-by-word while `active`. Resets each time `active` flips
 * true so reopening the panel re-streams. Skips the animation entirely under
 * prefers-reduced-motion.
 */
function useStreamingText(text: string, active: boolean) {
  const [shown, setShown] = useState(text)
  const [done, setDone] = useState(true)

  useEffect(() => {
    if (!active) return

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) {
      setShown(text)
      setDone(true)
      return
    }

    setShown('')
    setDone(false)
    // Split into word + whitespace tokens so newlines/bullets are preserved.
    const tokens = text.split(/(\s+)/)
    let i = 0
    const id = window.setInterval(() => {
      i += 2 // ~2 tokens per tick → smooth "typing" cadence
      if (i >= tokens.length) {
        setShown(text)
        setDone(true)
        window.clearInterval(id)
      } else {
        setShown(tokens.slice(0, i).join(''))
      }
    }, 28)

    return () => window.clearInterval(id)
  }, [text, active])

  return { shown, done }
}

export function StepHelpSheet({ task }: { task: OnboardingTask }) {
  const [open, setOpen] = useState(false)
  const definition = getStepDefinition(task.step)
  const help = getStepHelp(task.step)
  const label = definition?.label ?? task.step

  const composed = `${help.whatItDoes}\n\nReady to move on when:\n${help.readyWhen
    .map((r) => `•  ${r}`)
    .join('\n')}`

  const { shown, done } = useStreamingText(composed, open)

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          aria-label={`Explain "${label}"`}
          title="What is this step?"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Sparkles className="h-4 w-4" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-md">
        <SheetHeader className="space-y-3 border-b pb-4 text-left">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-[#1f7a99]" />
            Step guide
          </div>
          <SheetTitle className="flex items-center gap-2 text-lg">
            {label}
            <StepStatusBadge status={task.status} />
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto py-5">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
            {shown}
            {!done && (
              <span className="ml-0.5 inline-block h-4 w-2 translate-y-0.5 animate-pulse bg-[#1f7a99] align-middle" />
            )}
          </p>
        </div>

        <div className="border-t pt-3 text-xs text-muted-foreground">
          Generated guide · explains this step and what it needs to finish.
        </div>
      </SheetContent>
    </Sheet>
  )
}
